import "./App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Documents from "./pages/Documents";
import Onboarding from "./pages/Onboarding";
import Announcements from "./pages/Announcements";
import Support from "./pages/Support";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Equipment from "./pages/Equipment";
import Availability from "./pages/Availability";
import AdminApplicantData from "./pages/AdminApplicantData";
import Payroll from "./pages/Payroll";
import Metrics from "./pages/Metrics";
import Users from "./pages/Users";
import Training from "./pages/Training";
import Assessment from "./pages/Assessment";
import Certification from "./pages/Certification";
import Opportunities from "./pages/Opportunities";
import ClientJourney from "./pages/ClientJourney";
import AdminContent from "./pages/AdminContent";
import DocumentReview from "./pages/DocumentReview";
import Certificates from "./pages/Certificates";
import Reports from "./pages/Reports";
import NotificationsManager from "./pages/NotificationsManager";
import CamiManager from "./pages/CamiManager";
import SystemSettings from "./pages/SystemSettings";
import RecruiterDashboard from "./pages/RecruiterDashboard";
import RecruiterCandidates from "./pages/RecruiterCandidates";
import RecruiterDocuments from "./pages/RecruiterDocuments";
import SupervisorApplicants from "./pages/SupervisorApplicants";
import FormsDocuments from "./pages/FormsDocuments";
import FormsManager, { FormReviews } from "./pages/FormsManager";
import WorkforceDashboard from "./pages/WorkforceDashboard";
import AdminPrograms from "./pages/AdminPrograms";
import ClientCompanies from "./pages/ClientCompanies";
import PatriotPanel from "./pages/PatriotPanel";
import AuditLog from "./pages/AuditLog";
import ClientDashboard from "./pages/ClientDashboard";
import ClientPrograms from "./pages/ClientPrograms";
import ClientContractors from "./pages/ClientContractors";
import ClientWeeklyReport from "./pages/ClientWeeklyReport";
import MyProgram from "./pages/MyProgram";
import SupervisorHub from "./pages/SupervisorHub";
import PortalLayout from "./components/PortalLayout";
import { Toaster } from "./components/ui/toaster";

function roleClass(role) {
  if (role === "client_admin" || role === "client_user") return "client";
  if (role === "recruiter") return "recruiter";
  if (role === "administrator" || role === "admin") return "admin";
  if (role === "supervisor") return "supervisor";
  return "applicant";
}

function roleHome(role) {
  const c = roleClass(role);
  if (c === "applicant") return "/dashboard";
  if (c === "client") return "/client-dashboard";
  if (c === "recruiter" || c === "supervisor") return "/supervisor/applicants";
  return "/metrics";
}

function Protected({ children, allow }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-slate-400">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (allow && !allow.includes(roleClass(user.role))) {
    return <Navigate to={roleHome(user.role)} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        element={
          <Protected>
            <PortalLayout />
          </Protected>
        }
      >
        <Route path="/dashboard" element={<Protected allow={["applicant", "admin", "supervisor"]}><Dashboard /></Protected>} />
        <Route path="/onboarding" element={<Protected allow={["applicant", "admin", "supervisor"]}><Onboarding /></Protected>} />
        <Route path="/documents" element={<Protected allow={["applicant", "admin", "supervisor"]}><FormsDocuments /></Protected>} />
        <Route path="/training" element={<Protected allow={["applicant", "admin", "supervisor"]}><Training /></Protected>} />
        <Route path="/assessment" element={<Protected allow={["applicant", "admin", "supervisor"]}><Assessment /></Protected>} />
        <Route path="/certification" element={<Protected allow={["applicant", "admin", "supervisor"]}><Certification /></Protected>} />
        <Route path="/opportunities" element={<Protected allow={["applicant", "admin", "supervisor"]}><Opportunities /></Protected>} />
        <Route path="/client-journey" element={<Protected allow={["applicant", "admin", "supervisor"]}><ClientJourney /></Protected>} />
        <Route path="/my-program" element={<Protected allow={["applicant", "admin", "supervisor"]}><MyProgram /></Protected>} />
        <Route path="/announcements" element={<Announcements />} />
        <Route path="/support" element={<Support />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/equipment" element={<Protected allow={["applicant", "admin", "supervisor"]}><Equipment /></Protected>} />
        <Route path="/availability" element={<Protected allow={["applicant", "admin", "supervisor"]}><Availability /></Protected>} />
        <Route path="/admin/applicant-data" element={<Protected allow={["admin", "supervisor"]}><AdminApplicantData /></Protected>} />
        <Route path="/supervisor/applicants" element={<Protected allow={["supervisor", "recruiter", "admin", "administrator"]}><SupervisorApplicants /></Protected>} />
        <Route path="/supervisor/hub" element={<Protected allow={["supervisor", "recruiter", "admin", "administrator"]}><SupervisorHub /></Protected>} />
        <Route path="/metrics" element={<Protected allow={["admin", "supervisor"]}><Metrics /></Protected>} />
        <Route path="/admin/workforce" element={<Protected allow={["admin"]}><WorkforceDashboard /></Protected>} />
        <Route path="/admin/programs" element={<Protected allow={["admin"]}><AdminPrograms /></Protected>} />
        <Route path="/admin/clients" element={<Protected allow={["admin"]}><ClientCompanies /></Protected>} />
        <Route path="/admin/patriot" element={<Protected allow={["admin"]}><PatriotPanel /></Protected>} />
        <Route path="/admin/audit" element={<Protected allow={["admin"]}><AuditLog /></Protected>} />
        <Route path="/payroll" element={<Protected allow={["admin"]}><Payroll /></Protected>} />
        <Route path="/users" element={<Protected allow={["admin", "supervisor"]}><Users /></Protected>} />
        <Route path="/admin/content" element={<Protected allow={["admin", "supervisor"]}><AdminContent /></Protected>} />
        <Route path="/admin/documents" element={<Protected allow={["admin", "supervisor"]}><DocumentReview /></Protected>} />
        <Route path="/admin/forms" element={<Protected allow={["admin", "supervisor"]}><FormsManager /></Protected>} />
        <Route path="/admin/certificates" element={<Protected allow={["admin", "supervisor"]}><Certificates /></Protected>} />
        <Route path="/admin/reports" element={<Protected allow={["admin", "supervisor"]}><Reports /></Protected>} />
        <Route path="/admin/notifications" element={<Protected allow={["admin", "supervisor"]}><NotificationsManager /></Protected>} />
        <Route path="/admin/cami" element={<Protected allow={["admin", "supervisor"]}><CamiManager /></Protected>} />
        <Route path="/admin/system" element={<Protected allow={["admin", "supervisor"]}><SystemSettings /></Protected>} />
        <Route path="/client-dashboard" element={<Protected allow={["client"]}><ClientDashboard /></Protected>} />
        <Route path="/client-programs" element={<Protected allow={["client"]}><ClientPrograms /></Protected>} />
        <Route path="/client-contractors" element={<Protected allow={["client"]}><ClientContractors /></Protected>} />
        <Route path="/client-reports" element={<Protected allow={["client"]}><ClientWeeklyReport /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
